import L from 'leaflet';
import React from 'react';
import { Marker, Popup } from 'react-leaflet';
import * as Inovx from '@shared';
import { isBlackAndWhiteScanPosition } from '../helpers/viewMedia';

var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

L.Icon.Default.prototype.options.imagePath = `${UrlUtilities.getBaseApplicationUrl()}/images/leaflet/`

interface View360MarkerProps {
    name: string,
    position: L.LatLng,
    radius?: number,
    thumbnailUrl?: string,
    data?: any,   
    index: number,
    onMarkerClicked?: (view: IView360Detail, index: number) => void,
    onMarkerHighlighted?: (view: IView360Detail, index: number, highlighted: boolean) => void,
    showElements: boolean;
    isActive: boolean;
    activeAsCircle?: boolean;
    isHighlighted: boolean;
    isMiniMap?: boolean;
    rotationAngle: number;
    hasRotation: boolean;
    interactive?: boolean;
}

const View360Marker = (props: View360MarkerProps) => {
    const hoverTimer = React.useRef<NodeJS.Timeout>(null);
    const isHoveredRef = React.useRef(false);
    const markerRef = React.useRef(null);
    const [isHovered, setIsHovered] = React.useState(false);
    const [showPopup, setShowPopup] = React.useState(false);
    const hoverDelay = 500; // ms

    React.useEffect(() => () => {
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
        }
    }, []);

    React.useEffect(() => {
        if (showPopup) {
            markerRef.current?.openPopup();
        }
    }, [showPopup]);
   
    const handleMouseOver = React.useCallback(() => {
        if (isHoveredRef.current) return;

        isHoveredRef.current = true;
        setIsHovered(true);

        if (props.showElements && props.thumbnailUrl) {
            hoverTimer.current = setTimeout(() => {
                hoverTimer.current = null;
                setShowPopup(true);
            }, hoverDelay);
        }
        props.onMarkerHighlighted?.(props.data, props.index, true);
    }, [props.data, props.index, props.onMarkerHighlighted, props.showElements, props.thumbnailUrl]);

    const handleMouseOut = React.useCallback(() => {
        if (!isHoveredRef.current) return;

        isHoveredRef.current = false;
        setIsHovered(false);
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
            hoverTimer.current = null;
        }
        setShowPopup(false);
        if (markerRef.current) {
            markerRef.current.closePopup();
        }
        props.onMarkerHighlighted?.(props.data, props.index, false);
    }, [props.data, props.index, props.onMarkerHighlighted]);

    const handleMouseClick = React.useCallback(() => {
        props.onMarkerClicked?.(props.data, props.index);
    }, [props.data, props.index, props.onMarkerClicked]);

    const getActiveViewImageUrl = () => {
        return `${UrlUtilities.getBaseApplicationUrl()}/images/leaflet/direction.png`;
    }

    const getHighlightViewImageUrl = () => {
        return `${UrlUtilities.getBaseApplicationUrl()}/images/leaflet/highlight-mini-map-marker-icon.png`;
    }

    const getDefaultImageUrl = () => {
        return `${UrlUtilities.getBaseApplicationUrl()}/images/leaflet/mini-map-marker-icon.png`;
    }

    const getDefaultImage360Url = () => {
        return `${UrlUtilities.getBaseApplicationUrl()}/images/leaflet/mini-map-marker-360-icon.png`;
    }

    const getEnteredImage360Url = () => {
        return `${UrlUtilities.getBaseApplicationUrl()}/images/leaflet/mini-map-marker-360-entered.png`;
    }

    function createImage360Entered(url: string, iconAnchor: L.PointTuple) {
        return new L.Icon({
            iconUrl: url,
            className: "custom-arrow-marker",
            iconAnchor: iconAnchor
        });
    }

    function createIcon(url: string, iconAnchor: L.PointTuple) {
        return new L.Icon({
            iconUrl: url,
            className: "custom-arrow-marker",
            iconAnchor: iconAnchor
        });
    }  

    const createCustomIcon = (url: string, iconAnchor: L.PointTuple) => {
        return L.divIcon({
            iconUrl: url,
            iconAnchor: iconAnchor,
            className: 'custom-marker-icon',
            html: `<img src="${url}" style="transform: rotate(${props.rotationAngle}deg);" />`,
        });
    };

    const createBlackAndWhiteScanPositionIcon = (highlighted: boolean = false) => {
        const iconSize = 18;

        return L.divIcon({
            iconAnchor: [iconSize / 2, iconSize / 2],
            iconSize: [iconSize, iconSize],
            className: `custom-arrow-marker black-and-white-scan-position-marker${highlighted ? ' highlighted' : ''}`,
            html: false
        });
    };

    const markerIcon = React.useMemo(() => {
        const isHighlighted = props.isHighlighted || isHovered;

        if (props.isActive && props.activeAsCircle) {
            return createIcon(getHighlightViewImageUrl(), [9, 9]);
        }

        if (props.isActive)
            if (props.hasRotation) {
                return createCustomIcon(getActiveViewImageUrl(), [24, 24]);
            }
            else {
                return createImage360Entered(getEnteredImage360Url(), [12, 12]);
            }
        else if (isHighlighted)
            return props.hasRotation && isBlackAndWhiteScanPosition(props.data)
                ? createBlackAndWhiteScanPositionIcon(true)
                : createIcon(getHighlightViewImageUrl(), [9, 9]);
        else if (!props.hasRotation)
        {
            return createIcon(getDefaultImage360Url(), [9, 9]);
        }
        else
        {
            return isBlackAndWhiteScanPosition(props.data)
                ? createBlackAndWhiteScanPositionIcon()
                : createIcon(getDefaultImageUrl(), [9, 9]);
        }
    }, [isHovered, props.data, props.hasRotation, props.isActive, props.activeAsCircle, props.isHighlighted, props.rotationAngle]);

    const eventHandlers = React.useMemo(() => ({
        click: handleMouseClick,
        mouseover: handleMouseOver,
        mouseout: handleMouseOut,
    }), [handleMouseClick, handleMouseOut, handleMouseOver]);
    

    return (
        <Marker            
            key={props.index}
            icon={markerIcon}
            ref={markerRef}           
            position={props.position}
            interactive={props.interactive !== false && !(props.isMiniMap && props.isActive)}
            eventHandlers={eventHandlers}
            zIndexOffset={props.isMiniMap && props.isActive ? 0 : props.isActive ? 100 : 10}
            >

            {showPopup && props.thumbnailUrl && <div>
                <Popup className="map-tooltip" closeButton={false} autoPan={true} >
                    <div className="map-tooltip-container">
                        <div>{props.name}</div>
                        <img className="map-tooltip-img" src={props.thumbnailUrl} alt={props.name} />
                    </div>
                </Popup>
            </div>
            }
        </Marker>
    );
};

export default React.memo(View360Marker);
